/* @flow */
import 'isomorphic-fetch'
import { API, checkResponseForErrors } from '../../util/api'
import { clearSOMData as deviceClearSOMData } from '../device'
import { createDefaultHeaders } from '../../util/api'
import { getResolvedUserId } from '../../util/maskAsUser'
import { logger } from '../../util/logger'
import { set as setDevice } from '../device'
import type { GeoPoint } from '../location/types'
import * as actionCreators from './creators'
import * as locationActionCreators from '../location'
import * as MQTT from '../../util/mqtt'
import changeCase from 'change-object-case'
import URLSearchParams from 'url-search-params'

// Types
import type { Dispatch } from '../../util/redux'
import type { Environment } from '../../util/app'
import type { GetPreferenceCompleteAction } from './creators'
import type { Preference } from './types'

/**
 * Login user
 * @param {string} username - User's username
 * @param {string} password - User's password
 * @returns {Promise} - Logged in user's data
 */
export function login(username: string, password: string) {
  return function(dispatch: Dispatch) {
    const Api = new API()
    const authUrl = Api.authUrl + '/token'
    const inputData = {
      /* eslint-disable camelcase */
      grant_type: 'password',
      username: username,
      password: password,
      client_id: Api.clientID,
      client_secret: Api.clientSecret
      /* eslint-enable camelcase */
    }
    const params = new URLSearchParams()
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }

    for (const name in inputData) {
      params.append(name, inputData[name])
    }

    const fetchOptions = {
      method: 'POST',
      body: params.toString(),
      headers: headers
    }

    dispatch(actionCreators.loginInit())

    // Return call to auth service
    return fetch(authUrl, fetchOptions)
      .then(resp => checkResponseForErrors(authUrl, resp, fetchOptions))
      .then(response => response.json())
      .then(json => dispatch(actionCreators.loginComplete(json)))
      .catch(err => dispatch(actionCreators.loginComplete(err)))
  }
}

/**
 * Get single user preference
 * @param {string} bearer - User's access token
 * @param {string} userId - User's id
 * @param {string} preferenceName - Preference name
 * @returns {Promise} - Dispatched actions
 */
export const getPreference = (
  bearer: string,
  userId: string,
  preferenceName: string
): ((dispatch: Dispatch) => Promise<GetPreferenceCompleteAction>) => {
  return function(dispatch: Dispatch): Promise<GetPreferenceCompleteAction> {
    const Api = new API()
    const headers = createDefaultHeaders(bearer)
    const fetchOptions = { headers: headers }

    const resolvedUserId: string = getResolvedUserId(userId)

    dispatch(
      // $FlowFixMe
      actionCreators.getPreferenceInit({
        userId: resolvedUserId,
        preferenceName: preferenceName
      })
    )

    return (
      Api.call(
        `user/getUserPreference/${resolvedUserId}/${preferenceName}`,
        fetchOptions
      )
        .then(response => response.json())
        .then(json => {
          dispatch(actionCreators.setPreference({ ...json, userId }))

          return dispatch(
            // $FlowFixMe
            actionCreators.getPreferenceComplete({ ...json, userId })
          )
        })
        // $FlowFixMe
        .catch(err => dispatch(actionCreators.getPreferenceComplete(err)))
    )
  }
}

/**
 * Update user's preferences. NOTE: The API handles the preference type in a
 * really weird fashion, sending back things like 'STRING' for 'UNIT'.
 * Safest to default the type to 'STRING'.
 * @param {string} bearer - User's access token
 * @param {string} userId - User's id
 * @param {Object} preference - Preference object
 * @returns {Promise} - Dispatched actions
 */
export function updatePreferences(
  bearer: string,
  userId: string,
  preference: Preference
) {
  return function(dispatch: Dispatch) {
    const Api = new API()
    const reqObj = {
      userId: userId,
      preference: preference
    }
    const reqData = changeCase.snakeKeys(reqObj, { recursive: true })
    const headers = {
      ...createDefaultHeaders(bearer),
      'Content-Type': 'application/json'
    }
    const fetchOptions = {
      method: 'PUT',
      headers: headers,
      body: JSON.stringify(reqData)
    }

    dispatch(
      actionCreators.userUpdateInit({
        userId: userId,
        preference: preference,
        preference_name: preference.name // eslint-disable-line camelcase
      })
    )

    return Api.call('user/updateUserPreference', fetchOptions)
      .then(response => response.json())
      .then(json => {
        dispatch(actionCreators.set(json.user))
        if (preference.name === 'IS_METRIC') {
          dispatch(locationActionCreators.clearSOMData())
          dispatch(deviceClearSOMData())
        }
        return json
      })
      .then(json => dispatch(actionCreators.userUpdateComplete(json)))
      .catch(err => dispatch(actionCreators.userUpdateComplete(err)))
  }
}

/**
 * Update user's preferences. NOTE: The API handles the preference type in a
 * really weird fashion, sending back things like 'STRING' for 'UNIT'.
 * Safest to default the type to 'STRING'.
 * @param {string} bearer - User's access token
 * @param {string} userId - User's id
 * @param {Object} preferenceName - Name of preference
 * @returns {Promise} - [DESCRIPTION]
 */
export function deletePreference(
  bearer: string,
  userId: string,
  preferenceName: string
) {
  return function(dispatch: Dispatch) {
    dispatch(
      actionCreators.userUpdateInit({
        userId: userId,
        preferenceName: preferenceName
      })
    )

    const Api = new API()
    const reqObj = {
      userId: userId,
      preferenceName: preferenceName,
      delete: true
    }
    const reqData = changeCase.snakeKeys(reqObj, { recursive: true })
    const headers = createDefaultHeaders(bearer)
    const fetchOptions = {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(reqData)
    }

    return Api.call('UserService/UpdateUserPreference', fetchOptions)
      .then(response => {
        return response.json()
      })
      .then(json => dispatch(actionCreators.userUpdateComplete(json)))
      .catch(err => dispatch(actionCreators.userUpdateComplete(err)))
  }
}

/* eslint-disable max-len */
/**
 * Subscribe to user events
 * @param {string} url - MQTT URL
 * @param {('DEVELOPMENT'|'PRODUCTION')} env - Environment ID
 * @param {string} userId - User ID
 * @param {function} [messageHandler] - Function to call when a message is received; optional
 * @returns {object} - MQTT client
 */
export const subscribeToUpdates = (
  url: string,
  env: Environment,
  userId: string,
  messageHandler: (any, any) => Promise<any>
): Object => {
  /* eslint-enable max-len */
  const envId = env === 'DEVELOPMENT' ? 'dev' : 'prod'
  // $FlowFixMe
  const clientId = MQTT.createClientId(envId, userId)
  const topicId = MQTT.createTopicId(userId)
  const client = MQTT.createMqttClient(url, topicId, clientId)

  if (messageHandler) {
    client.on('message', messageHandler)
  }

  client.on('error', e => {
    if (!client.connected && !client.reconnecting) {
      logger.info('MQTT client reconnecting', { error: e, client })
      client.reconnect()
    }
  })

  return client
}

/**
 * Get list of locations for user
 * @param {string} authToken - User Auth Token
 * @param {string} userId - User ID
 * @param {boolean} [includeState=true] - Whether or not to include location states
 * @returns {function} - Dispatch function
 */
export const getLocations = (
  authToken: string,
  userId: string,
  includeState: boolean = true
) => {
  return function(dispatch: Dispatch) {
    const Api = new API()
    const headers = {
      ...createDefaultHeaders(authToken),
      'Content-Type': 'application/json'
    }

    dispatch(actionCreators.getLocationsInit({ userId }))

    return Api.call('location/listLocations/' + includeState.toString(), {
      method: 'GET',
      headers: headers
    })
      .then(response => response.json())
      .then(json => {
        const locationIds = json.locationSummary.map(
          location => location.location.id
        )
        // Update locations in store
        dispatch(locationActionCreators.up(json.locationSummary))
        // Set locations in user
        return dispatch(
          actionCreators.getLocationsComplete({
            userId: userId,
            locationIds: locationIds
          })
        )
      })
      .catch(err => {
        err.userId = userId
        return dispatch(actionCreators.getLocationsComplete(err))
      })
  }
}

/**
 * Get list of devices for user
 * @param {string} authToken - User Auth Token
 * @param {string} userId - User ID
 * @returns {function} - Dispatch function
 */
export const getDevices = (authToken: string, userId: string) => {
  return function(dispatch: Dispatch) {
    const Api = new API()
    const headers = {
      ...createDefaultHeaders(authToken),
      'Content-Type': 'application/json'
    }

    dispatch(actionCreators.getDevicesInit({ userId }))

    return Api.call(`device/listDevicesByOwner/${userId}`, {
      method: 'GET',
      headers: headers
    })
      .then(response => response.json())
      .then(json => {
        const deviceIds = json.device.map(device => device.id)
        dispatch(setDevice(json.device)) // This should probably be an update
        dispatch(actionCreators.setDevicesIds({ userId, deviceIds }))
        return dispatch(
          actionCreators.getDevicesComplete({
            userId: userId,
            devices: json.device
          })
        )
      })
      .catch(err => {
        err.userId = userId
        return dispatch(actionCreators.getDevicesComplete(err))
      })
  }
}

/**
 * updatePassword - Update password in user profile
 *
 * @param {string} bearer - User Auth Token
 * @param {string} userId - User ID
 * @param {string} oldPassword User's current password
 * @param {string} newPassword Desired new password
 *
 * @returns {function} Dispatch function
 */
export function updatePassword(
  bearer: string,
  userId: string,
  oldPassword: string,
  newPassword: string
) {
  return function(dispatch: Dispatch) {
    const Api = new API()
    const reqObj = { userId, oldPassword, newPassword }
    const reqData = changeCase.snakeKeys(reqObj, { recursive: true })
    const headers = {
      ...createDefaultHeaders(bearer),
      'Content-Type': 'application/json'
    }
    const fetchOptions = {
      method: 'PUT',
      headers: headers,
      body: JSON.stringify(reqData)
    }

    dispatch(actionCreators.updatePasswordInit({ userId }))

    return Api.call('user/updatePassword', fetchOptions)
      .then(response => {
        return response.json()
      })
      .then(json => dispatch(actionCreators.updatePasswordComplete(json)))
      .catch(err => dispatch(actionCreators.updatePasswordComplete(err)))
  }
}

/**
 * send forgot password email
 * @param {string} emailAddress Email address entered by user
 * @returns {func} Dispatch function
 */
export function sendResetPasswordEmail(emailAddress: string) {
  return function() {
    const Api = new API()
    const reqObj = changeCase.snakeKeys({ emailAddress }, { recursive: true })
    const headers = {
      ...createDefaultHeaders(),
      'Content-Type': 'application/json'
    }
    const fetchOptions = {
      method: 'POST',
      headers,
      body: JSON.stringify(reqObj)
    }

    return Api.call('user/SendResetPasswordEmail', fetchOptions)
  }
}

/**
 * reset user password
 * @param {string} token token for backend to identify user with
 * @param {string} password new password
 * @returns {func} Dispatch function
 */
export function resetPassword(token: string, password: string) {
  return function() {
    const Api = new API()
    const reqObj = changeCase.snakeKeys(
      { token, password },
      { recursive: true }
    )
    const headers = {
      ...createDefaultHeaders(),
      'Content-Type': 'application/json'
    }
    const fetchOptions = {
      method: 'POST',
      headers,
      body: JSON.stringify(reqObj)
    }

    return Api.call('user/ResetPassword', fetchOptions).then(res => res.json())
  }
}

/**
 * check that the token for the reset forgot password screen is valid
 * @param {string} token - reset password token from email
 * @return {Function} - Thunk
 */
export function getResetPasswordTokenStatus(token: string) {
  return function() {
    const Api = new API()
    const headers = {
      ...createDefaultHeaders(),
      'Content-Type': 'application/json'
    }
    const fetchOptions = {
      method: 'GET',
      headers
    }

    return Api.call(
      'user/ResetPasswordTokenStatus/' + token,
      fetchOptions
    ).then(res => res.json())
  }
}

/**
 * Get user's public API key
 * @param {string} token - User's auth token
 * @param {string} userId - User's ID
 * @return {Function} - Thunk
 */
export const getPublicApiKey = (token: string, userId: string) => {
  return function(dispatch: Dispatch) {
    const Api = new API()
    const headers = {
      ...createDefaultHeaders(token),
      'Content-Type': 'application/json'
    }
    const fetchOptions = {
      method: 'GET',
      headers
    }

    actionCreators.getApiKeyInit({ userId })

    return Api.call('user/getUserApiToken/' + userId, fetchOptions)
      .then(res => res.json())
      .then(json => {
        return dispatch(actionCreators.getApiKeyComplete({ userId, ...json }))
      })
  }
}

type CreateLocationRequestForVIC = {
  name: string,
  geoPoint: GeoPoint
}

type CreateVICRequest = {
  name: string,
  serialNumber: string,
  geoPoint: GeoPoint,
  model: string
}

export type CreateUserAndVICRequest = {
  emailAddress: string,
  password: string,
  location: CreateLocationRequestForVIC,
  controller: CreateVICRequest
}

export const createUserAndVIC = (body: CreateUserAndVICRequest) => {
  const Api = new API()
  const fetchOptions = {
    method: 'POST',
    headers: {
      ...createDefaultHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  }

  return Api.call(
    'user/createUserAndVirtualIrrigationController',
    fetchOptions
  ).then(res => res.json())
}
